import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Flex,
  Text,
  useColorModeValue,
  Input,
  InputGroup,
  InputLeftElement,
  useToast,
  VStack,
  HStack,
  Image,
  Spinner,
  Badge,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  FormErrorMessage,
  useDisclosure,
  IconButton,
  Tooltip,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  Select,
} from "@chakra-ui/react";
import {
  SearchIcon,
  AddIcon,
  EditIcon,
  DeleteIcon,
  ChevronRightIcon,
  ChevronDownIcon,
} from "@chakra-ui/icons";
import { FolderIcon, FolderOpenIcon } from "components/Icons/Icons.js";
import Card from "components/Card/Card.js";
import { categoriesService } from "api";

const TreeNode = ({ 
  category, 
  level = 0, 
  onEdit, 
  onDelete, 
  expandedNodes, 
  onToggleExpand,
  onAddChild 
}) => {
  // Check if category has children - be more strict about this
  const hasChildren = (category.children && category.children.length > 0) || 
                     (category.has_children === true && category.children_count > 0) ||
                     (category.children_count && category.children_count > 0);
  const isExpanded = expandedNodes.includes(category.id);
  const isLoadingChildren = isExpanded && hasChildren && (!category.children || category.children.length === 0);
  const textColor = useColorModeValue("gray.700", "white");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const hoverBg = useColorModeValue("gray.50", "navy.700");

  return (
    <Box>
      {/* Main category row with stair-like indentation */}
      <Box
        mr={`${level * 32}px`} // Changed from ml to mr for right-side indentation
        position="relative"
        mb={2}
      >
        {/* Vertical line connecting to parent */}
        {level > 0 && (
          <Box
            position="absolute"
            right="-16px" // Changed from left to right
            top="0"
            bottom="0"
            w="2px"
            bg="blue.300"
            borderRadius="full"
          />
        )}
        
        {/* Horizontal line connecting to parent */}
        {level > 0 && (
          <Box
            position="absolute"
            right="-16px" // Changed from left to right
            top="50%"
            w="16px"
            h="2px"
            bg="blue.300"
            borderRadius="full"
          />
        )}
        
        <Flex
          align="center"
          p={4}
          bg={level === 0 ? "white" : level === 1 ? "blue.50" : level === 2 ? "blue.100" : "blue.150"}
          border="1px solid"
          borderColor={borderColor}
          borderRadius="lg"
          _hover={{ bg: hoverBg, transform: "translateX(4px)" }}
          transition="all 0.2s"
          cursor={hasChildren ? "pointer" : "default"}
          onClick={() => hasChildren && onToggleExpand(category.id)}
          position="relative"
          minH="70px"
          boxShadow="sm"
        >
          {/* Expand/collapse arrow */}
          <Box mr={3}>
            {hasChildren ? (
              isExpanded ? (
                <ChevronDownIcon color="blue.500" w={5} h={5} />
              ) : (
                <ChevronRightIcon color="blue.500" w={5} h={5} />
              )
            ) : (
              <Box w={5} />
            )}
          </Box>
          
          {/* Folder icon */}
          <Box mr={3}>
            <FolderIcon
              color={isExpanded ? "blue.500" : "gray.500"}
              w={6}
              h={6}
            />
          </Box>
          
          {/* Category image */}
          {category.image && (
            <Box mr={3}>
              <Image
                src={category.image}
                alt={category.name}
                w={10}
                h={10}
                borderRadius="md"
                objectFit="cover"
                border="2px solid"
                borderColor="gray.200"
              />
            </Box>
          )}
          
          {/* Category name and info */}
          <Box flex={1}>
            <Text color={textColor} fontWeight="semibold" fontSize="md">
              {category.name}
            </Text>
            {category.parent && (
              <Text color="gray.500" fontSize="sm">
                Parent: {category.parent.name}
              </Text>
            )}
          </Box>
          
          {/* Action buttons */}
          <HStack spacing={2}>
            <Button
              size="sm"
              colorScheme="green"
              variant="ghost"
              onClick={(e) => {
                e.stopPropagation();
                onAddChild(category);
              }}
            >
              إضافة
            </Button>
            
            <Button
              size="sm"
              colorScheme="blue"
              variant="ghost"
              onClick={(e) => {
                e.stopPropagation();
                onEdit(category);
              }}
            >
              تعديل
            </Button>
            
            <Button
              size="sm"
              colorScheme="red"
              variant="ghost"
              onClick={(e) => {
                e.stopPropagation();
                onDelete(category);
              }}
            >
              حذف
            </Button>
          </HStack>
        </Flex>
      </Box>

      {/* Children section */}
      {hasChildren && isExpanded && (
        <Box>
          {isLoadingChildren ? (
            <Box mr={`${(level + 1) * 32}px`}> {/* Changed from ml to mr */}
              <Flex justify="center" align="center" p={4}>
                <Spinner size="sm" color="blue.500" />
                <Text ml={2} color="gray.500" fontSize="sm">جاري تحميل الفئات الفرعية...</Text>
              </Flex>
            </Box>
          ) : category.children && category.children.length > 0 ? (
            category.children.map((child) => (
              <TreeNode
                key={child.id}
                category={child}
                level={level + 1}
                onEdit={onEdit}
                onDelete={onDelete}
                expandedNodes={expandedNodes}
                onToggleExpand={onToggleExpand}
                onAddChild={onAddChild}
              />
            ))
          ) : (
            <Box mr={`${(level + 1) * 32}px`}> {/* Changed from ml to mr */}
              <Flex justify="center" align="center" p={4}>
                <Text color="gray.400" fontSize="sm">لا توجد فئات فرعية</Text>
              </Flex>
            </Box>
          )}
        </Box>
      )}
      
      {/* Message for categories without children */}
      {isExpanded && !hasChildren && (
        <Box mr={`${(level + 1) * 32}px`}> {/* Changed from ml to mr */}
          <Flex justify="center" align="center" p={4}>
            <Text color="gray.400" fontSize="sm">لا توجد فئات فرعية</Text>
          </Flex>
        </Box>
      )}
    </Box>
  );
};

// Category Modal Component
function CategoryModal({ isOpen, onClose, category, onSubmit, actionLoading, validationErrors }) {
  const [formData, setFormData] = useState({
    en_name: "",
    ar_name: "",
    parent_category_id: "",
    image: null
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [imagePreviewUrl, setImagePreviewUrl] = useState(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [availableParentCategories, setAvailableParentCategories] = useState([]);
  const [isLoadingParents, setIsLoadingParents] = useState(false);
  const toast = useToast();

  // Fetch available parent categories
  const fetchParentCategories = async () => {
    setIsLoadingParents(true);
    try {
      const response = await categoriesService.getCategories();
      if (response.success) {
        const categoriesData = response.data?.data || response.data || [];
        
        // Filter out the current category being edited and prevent circular references
        let filteredCategories = categoriesData;
        
        if (category?.id) {
          // When editing, exclude the current category and any of its descendants
          const excludeIds = new Set([category.id]);
          
          // Helper function to collect all descendant IDs
          const collectDescendantIds = (catId, cats) => {
            const cat = cats.find(c => c.id === catId);
            if (cat && cat.children) {
              cat.children.forEach(child => {
                excludeIds.add(child.id);
                collectDescendantIds(child.id, cats);
              });
            }
          };
          
          // Collect all descendant IDs to prevent circular references
          collectDescendantIds(category.id, categoriesData);
          
          filteredCategories = categoriesData.filter(cat => !excludeIds.has(cat.id));
        }
        
        setAvailableParentCategories(filteredCategories);
      }
    } catch (error) {
      console.error('Error fetching parent categories:', error);
      toast({
        title: "خطأ",
        description: "حدث خطأ أثناء جلب الفئات المتاحة",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsLoadingParents(false);
    }
  };

  useEffect(() => {
    if (category?.id) {
      // Editing existing category
      setFormData({
        en_name: category.en_name || "",
        ar_name: category.ar_name || "",
        parent_category_id: category.parent_category_id || "",
        image: null // Reset image for editing (user can choose to keep or change)
      });
    } else if (category?.parent_id) {
      // Creating child category
      setFormData({
        en_name: "",
        ar_name: "",
        parent_category_id: category.parent_id,
        image: null
      });
    } else {
      // Creating root category - set parent_category_id to empty string
      setFormData({
        en_name: "",
        ar_name: "",
        parent_category_id: "", // Use empty string for root categories
        image: null
      });
    }
    
    // Fetch available parent categories
    if (isOpen) {
      fetchParentCategories();
    }
  }, [category]); // Remove isOpen from dependencies to prevent image reset

  // Separate effect for fetching parent categories when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchParentCategories();
    }
  }, [isOpen]);

  // Cleanup effect for image preview URL
  useEffect(() => {
    if (formData.image instanceof File) {
      const url = URL.createObjectURL(formData.image);
      setImagePreviewUrl(url);
      
      // Cleanup function to revoke the URL
      return () => {
        if (url) {
          URL.revokeObjectURL(url);
        }
      };
    } else {
      setImagePreviewUrl(null);
    }
  }, [formData.image]);

  // Cleanup effect when modal closes
  useEffect(() => {
    if (!isOpen) {
      // Clean up any remaining preview URLs when modal closes
      if (imagePreviewUrl) {
        URL.revokeObjectURL(imagePreviewUrl);
        setImagePreviewUrl(null);
      }
      // Reset drag state
      setIsDragOver(false);
    }
  }, [isOpen, imagePreviewUrl]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.ar_name.trim() || !formData.en_name.trim()) {
      return;
    }

    // Debug: Log the formData before processing
    console.log('CategoryModal: Raw formData before processing:', formData);
    console.log('CategoryModal: formData.image type:', typeof formData.image);
    console.log('CategoryModal: formData.image instanceof File:', formData.image instanceof File);
    console.log('CategoryModal: formData.image value:', formData.image);

    // Additional validation for image
    if (formData.image && formData.image instanceof File) {
      // Validate file type again
      if (!formData.image.type.startsWith('image/')) {
        toast({
          title: "خطأ",
          description: "يرجى اختيار ملف صورة صحيح",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
        return;
      }
      
      // Validate file size again
      const maxSize = 5 * 1024 * 1024; // 5MB
      if (formData.image.size > maxSize) {
        toast({
          title: "خطأ",
          description: "حجم الصورة يجب أن يكون أقل من 5 ميجابايت",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
        return;
      }
    }

    setIsSubmitting(true);
    try {
      // Clean the form data before submission
      const cleanFormData = {
        en_name: formData.en_name.trim(),
        ar_name: formData.ar_name.trim(),
        image: formData.image
      };
      
      // Handle parent_category_id - convert empty string to null for root categories
      cleanFormData.parent_category_id = formData.parent_category_id === "" ? null : formData.parent_category_id;

      // Debug: Log the cleanFormData before sending
      console.log('CategoryModal: Clean formData before sending:', cleanFormData);
      console.log('CategoryModal: cleanFormData.image type:', typeof cleanFormData.image);
      console.log('CategoryModal: cleanFormData.image instanceof File:', cleanFormData.image instanceof File);
      console.log('CategoryModal: cleanFormData.image value:', cleanFormData.image);

      // Log what's being sent for debugging
      if (category?.id) {
        console.log('CategoryModal: Updating category with data:', cleanFormData);
        if (cleanFormData.image) {
          console.log('CategoryModal: Image will be updated');
        } else if (category.image) {
          console.log('CategoryModal: Keeping existing image');
        } else {
          console.log('CategoryModal: No image in this category');
        }
      } else {
        console.log('CategoryModal: Creating new category with data:', cleanFormData);
      }

      if (category?.id) {
        await onSubmit(category.id, cleanFormData);
      } else {
        await onSubmit(cleanFormData);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Debug: Log the file being selected
      console.log('CategoryModal: File selected:', file);
      console.log('CategoryModal: File type:', file.type);
      console.log('CategoryModal: File size:', file.size);
      console.log('CategoryModal: File instanceof File:', file instanceof File);
      
      // Validate file type
      if (!file.type.startsWith('image/')) {
        toast({
          title: "خطأ",
          description: "يرجى اختيار ملف صورة صحيح",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
        return;
      }
      
      // Validate file size (5MB limit)
      const maxSize = 5 * 1024 * 1024; // 5MB
      if (file.size > maxSize) {
        toast({
          title: "خطأ",
          description: "حجم الصورة يجب أن يكون أقل من 5 ميجابايت",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
        return;
      }
      
      const newFormData = { ...formData, image: file };
      console.log('CategoryModal: Setting new formData with image:', newFormData);
      setFormData(newFormData);
    }
  };

  const handleRemoveImage = () => {
    // Clean up the preview URL if it exists
    if (imagePreviewUrl) {
      URL.revokeObjectURL(imagePreviewUrl);
      setImagePreviewUrl(null);
    }
    setFormData({ ...formData, image: null });
    // Reset the file input
    const fileInput = document.getElementById('category-image-input');
    if (fileInput) {
      fileInput.value = '';
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      const file = files[0];
      console.log('CategoryModal: File dropped:', file);
      console.log('CategoryModal: Dropped file type:', file.type);
      console.log('CategoryModal: Dropped file size:', file.size);
      console.log('CategoryModal: Dropped file instanceof File:', file instanceof File);
      
      if (file.type.startsWith('image/')) {
        // Validate file size (5MB limit)
        const maxSize = 5 * 1024 * 1024; // 5MB
        if (file.size > maxSize) {
          toast({
            title: "خطأ",
            description: "حجم الصورة يجب أن يكون أقل من 5 ميجابايت",
            status: "error",
            duration: 3000,
            isClosable: true,
          });
          return;
        }
        const newFormData = { ...formData, image: file };
        console.log('CategoryModal: Setting new formData with dropped image:', newFormData);
        setFormData(newFormData);
      } else {
        toast({
          title: "خطأ",
          description: "يرجى اختيار ملف صورة صحيح",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      }
    }
  };

  const getImagePreview = () => {
    if (imagePreviewUrl) {
      return imagePreviewUrl;
    } else if (category?.image && !formData.image) {
      return category.image;
    }
    return null;
  };

  const getCurrentImageInfo = () => {
    if (formData.image instanceof File) {
      return {
        src: imagePreviewUrl,
        isNew: true,
        name: formData.image.name
      };
    } else if (category?.image && !formData.image) {
      return {
        src: category.image,
        isNew: false,
        name: 'Current Image'
      };
    }
    return null;
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay bg="blackAlpha.300" backdropFilter="blur(10px)" />
      <ModalContent
        mx="20px"
        maxW="500px"
        zIndex={9999}
        position="relative"
        borderRadius="15px"
        boxShadow="0 20px 60px rgba(0, 0, 0, 0.3)"
        border="1px solid"
        borderColor="gray.200"
      >
        <form onSubmit={handleSubmit}>
          <ModalHeader
            borderBottom="1px solid"
            borderColor="gray.200"
            pb="20px"
            fontSize="xl"
            fontWeight="bold"
            textAlign="center"
          >
            {category?.id ? `تعديل الفئة: ${category.ar_name || category.en_name}` : category?.parent_id ? "إضافة فئة فرعية" : "إضافة فئة جديدة"}
          </ModalHeader>
          <ModalCloseButton
            top="20px"
            right="20px"
            size="lg"
            borderRadius="50%"
            bg="gray.100"
            _hover={{ bg: "gray.200" }}
          />
          <ModalBody pt="20px" pb="30px">
            <FormControl mb="20px" isInvalid={!!validationErrors.en_name}>
              <FormLabel fontWeight="600" mb="8px" color="gray.700">
                الاسم الإنجليزي
              </FormLabel>
              <Input
                value={formData.en_name}
                onChange={(e) => setFormData({ ...formData, en_name: e.target.value })}
                placeholder="أدخل الاسم الإنجليزي"
                required
                size="lg"
                borderRadius="8px"
                border="2px solid"
                borderColor={validationErrors.en_name ? "red.500" : "gray.200"}
                _focus={{ borderColor: "blue.500", boxShadow: "0 0 0 1px blue.500" }}
                _hover={{ borderColor: validationErrors.en_name ? "red.400" : "gray.300" }}
              />
              <FormErrorMessage textAlign="right">
                {validationErrors.en_name?.[0]}
              </FormErrorMessage>
            </FormControl>

            <FormControl mb="20px" isInvalid={!!validationErrors.ar_name}>
              <FormLabel fontWeight="600" mb="8px" color="gray.700">
                الاسم العربي
              </FormLabel>
              <Input
                value={formData.ar_name}
                onChange={(e) => setFormData({ ...formData, ar_name: e.target.value })}
                placeholder="أدخل الاسم العربي"
                required
                size="lg"
                borderRadius="8px"
                border="2px solid"
                borderColor={validationErrors.ar_name ? "red.500" : "gray.200"}
                _focus={{ borderColor: "blue.500", boxShadow: "0 0 0 1px blue.500" }}
                _hover={{ borderColor: validationErrors.ar_name ? "red.400" : "gray.300" }}
              />
              <FormErrorMessage textAlign="right">
                {validationErrors.ar_name?.[0]}
              </FormErrorMessage>
            </FormControl>

            {!category?.parent_id && (
              <FormControl mb="20px" isInvalid={!!validationErrors.parent_category_id}>
                <FormLabel fontWeight="600" mb="8px" color="gray.700">
                  الفئة الأب (اختياري)
                </FormLabel>
                {isLoadingParents ? (
                  <Flex align="center" p={4} bg="gray.50" borderRadius="8px">
                    <Spinner size="sm" color="blue.500" mr={3} />
                    <Text color="gray.600" fontSize="sm">جاري تحميل الفئات المتاحة...</Text>
                  </Flex>
                ) : (
                  <>
                    <Select
                      value={formData.parent_category_id}
                      onChange={(e) => setFormData({ ...formData, parent_category_id: e.target.value })}
                      placeholder="اختر الفئة الأب (اختياري)"
                      size="lg"
                      borderRadius="8px"
                      border="2px solid"
                      borderColor={validationErrors.parent_category_id ? "red.500" : "gray.200"}
                      _focus={{ borderColor: "blue.500", boxShadow: "0 0 0 1px blue.500" }}
                      _hover={{ borderColor: validationErrors.parent_category_id ? "red.400" : "gray.300" }}
                    >
                      <option value="">بدون فئة أب (فئة رئيسية)</option>
                      {availableParentCategories.map((parentCat) => (
                        <option key={parentCat.id} value={parentCat.id}>
                          {parentCat.ar_name || parentCat.en_name || parentCat.name}
                        </option>
                      ))}
                    </Select>
                    <Text fontSize="sm" color="gray.500" mt={2} textAlign="right">
                      اختر فئة أب لإنشاء فئة فرعية، أو اتركه فارغاً لإنشاء فئة رئيسية
                    </Text>
                    {formData.parent_category_id && (
                      <Text fontSize="sm" color="blue.600" mt={2} textAlign="right">
                        الفئة المختارة: {availableParentCategories.find(cat => cat.id == formData.parent_category_id)?.ar_name || availableParentCategories.find(cat => cat.id == formData.parent_category_id)?.en_name || 'Unknown'}
                      </Text>
                    )}
                  </>
                )}
                <FormErrorMessage textAlign="right">
                  {validationErrors.parent_category_id?.[0]}
                </FormErrorMessage>
              </FormControl>
            )}

                         <FormControl isInvalid={!!validationErrors.image}>
               <FormLabel fontWeight="600" mb="8px" color="gray.700">
                 الصورة (اختيارية)
               </FormLabel>
               
               {/* Image Preview */}
               {getCurrentImageInfo() && (
                 <Box mb={3} position="relative">
                   <Image
                     src={getCurrentImageInfo().src}
                     alt="Preview"
                     w="100px"
                     h="100px"
                     borderRadius="md"
                     objectFit="cover"
                     border="2px solid"
                     borderColor="gray.200"
                   />
                   <Box
                     position="absolute"
                     top="-8px"
                     right="-8px"
                     bg={getCurrentImageInfo().isNew ? "green.500" : "blue.500"}
                     color="white"
                     fontSize="xs"
                     px={2}
                     py={1}
                     borderRadius="full"
                     fontWeight="bold"
                   >
                     {getCurrentImageInfo().isNew ? "جديد" : "حالي"}
                   </Box>
                   <IconButton
                     icon={<DeleteIcon />}
                     size="sm"
                     colorScheme="red"
                     variant="solid"
                     position="absolute"
                     bottom="-8px"
                     right="-8px"
                     borderRadius="full"
                     onClick={handleRemoveImage}
                     aria-label="Remove image"
                   />
                 </Box>
               )}
               
               {/* Help text for editing */}
               {category?.id && category?.image && !formData.image && (
                 <Text fontSize="sm" color="blue.600" mb={3} textAlign="center">
                   الصورة الحالية ستُحفظ إذا لم تختر صورة جديدة
                 </Text>
               )}
               
               {/* Drag and Drop Area */}
               <Box
                 border="2px dashed"
                 borderColor={isDragOver ? "blue.400" : validationErrors.image ? "red.300" : "gray.300"}
                 borderRadius="lg"
                 p={6}
                 textAlign="center"
                 bg={isDragOver ? "blue.50" : validationErrors.image ? "red.50" : "gray.50"}
                 _hover={{
                   borderColor: validationErrors.image ? "red.400" : "blue.400",
                   bg: validationErrors.image ? "red.100" : "blue.50"
                 }}
                 transition="all 0.2s"
                 cursor="pointer"
                 onClick={() => document.getElementById('category-image-input').click()}
                 onDragOver={handleDragOver}
                 onDrop={handleDrop}
                 onDragLeave={handleDragLeave}
                 transform={isDragOver ? "scale(1.02)" : "scale(1)"}
                 boxShadow={isDragOver ? "0 4px 12px rgba(59, 130, 246, 0.15)" : "none"}
               >
                 <VStack spacing={2}>
                   <Box
                     w="40px"
                     h="40px"
                     borderRadius="full"
                     bg={isDragOver ? "blue.300" : validationErrors.image ? "red.200" : "blue.200"}
                     display="flex"
                     alignItems="center"
                     justifyContent="center"
                   >
                     <Image
                       src="/src/assets/svg/document.svg"
                       alt="Upload"
                       w="20px"
                       h="20px"
                       opacity={0.7}
                     />
                   </Box>
                   <Text fontSize="sm" color="gray.600" fontWeight="medium">
                     {isDragOver ? "أفلت الصورة هنا" : category?.id ? "انقر لتغيير الصورة أو اسحب وأفلت" : "انقر لاختيار صورة أو اسحب وأفلت"}
                   </Text>
                   <Text fontSize="xs" color="gray.500">
                     الصيغ المدعومة: JPG, PNG, GIF. الحد الأقصى: 5 ميجابايت
                   </Text>
                 </VStack>
               </Box>
               
               <Input
                 id="category-image-input"
                 type="file"
                 accept="image/*"
                 onChange={handleImageChange}
                 size="lg"
                 borderRadius="8px"
                 border="2px solid"
                 borderColor={validationErrors.image ? "red.500" : "gray.200"}
                 _focus={{ borderColor: "blue.500", boxShadow: "0 0 0 1px blue.500" }}
                 _hover={{ borderColor: validationErrors.image ? "red.400" : "gray.300" }}
                 display="none"
               />
               <FormErrorMessage textAlign="right">
                 {validationErrors.image?.[0]}
               </FormErrorMessage>
             </FormControl>
          </ModalBody>
          <ModalFooter
            borderTop="1px solid"
            borderColor="gray.200"
            pt="20px"
            gap="15px"
          >
            <Button
              variant="outline"
              onClick={onClose}
              size="lg"
              minW="120px"
              borderRadius="8px"
              border="2px solid"
              borderColor="gray.300"
              _hover={{ bg: "gray.50", borderColor: "gray.400" }}
            >
              إلغاء
            </Button>
            <Button
              colorScheme="blue"
              type="submit"
              size="lg"
              minW="120px"
              borderRadius="8px"
              isLoading={isSubmitting || actionLoading.create || actionLoading.update}
              loadingText={category?.id ? "جاري التحديث..." : "جاري الإنشاء..."}
              _hover={{ transform: "translateY(-1px)", boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)" }}
              disabled={isSubmitting}
            >
              {category?.id ? "تحديث" : "إنشاء"}
            </Button>
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  );
}

export default function Categories() {
  const textColor = useColorModeValue("gray.700", "white");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const cardBg = useColorModeValue("white", "navy.700");
  
  const [categories, setCategories] = useState([]);
  const [filteredCategories, setFilteredCategories] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [expandedNodes, setExpandedNodes] = useState([]);
  const [editingCategory, setEditingCategory] = useState(null);
  const [deletingCategory, setDeletingCategory] = useState(null);
  const [actionLoading, setActionLoading] = useState({});
  const [validationErrors, setValidationErrors] = useState({});
  
  const { isOpen: isModalOpen, onOpen: onModalOpen, onClose: onModalClose } = useDisclosure();
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
  
  const toast = useToast();

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    if (searchTerm.trim() === "") {
      setFilteredCategories(categories);
    } else {
      const filtered = filterCategoriesRecursively(categories, searchTerm);
      setFilteredCategories(filtered);
    }
  }, [searchTerm, categories]);

  // Clean up expanded nodes when categories change
  useEffect(() => {
    if (categories.length > 0 && expandedNodes.length > 0) {
      const cleanedExpandedNodes = cleanupExpandedNodes(categories, expandedNodes);
      if (cleanedExpandedNodes.length !== expandedNodes.length) {
        console.log('Cleaning up expanded nodes:', {
          before: expandedNodes.length,
          after: cleanedExpandedNodes.length
        });
        setExpandedNodes(cleanedExpandedNodes);
      }
    }
  }, [categories, expandedNodes]);

  const filterCategoriesRecursively = (cats, term) => {
    return cats.filter(cat => {
      const matchesName = cat.name.toLowerCase().includes(term.toLowerCase());
      const hasMatchingChildren = cat.children && cat.children.length > 0 && 
        filterCategoriesRecursively(cat.children, term).length > 0;
      return matchesName || hasMatchingChildren;
    });
  };

  const fetchCategories = async () => {
    setIsLoading(true);
    try {
      // Try to get tree structure first, fallback to flat structure
      let response = await categoriesService.getCategoriesTree();
      console.log('Categories tree API response:', response);
      
      if (!response.success) {
        // Fallback to regular categories
        response = await categoriesService.getCategories();
        console.log('Categories fallback API response:', response);
      }
      
      if (response.success) {
        // Handle both nested data structure and direct data
        const categoriesData = response.data?.data || response.data || [];
        console.log('Processed categories data:', categoriesData);
        
        if (Array.isArray(categoriesData)) {
          setCategories(categoriesData);
          setFilteredCategories(categoriesData);
          const rootIds = categoriesData.filter(cat => !cat.parent_category_id).map(cat => cat.id);
          // Clean up expanded nodes to only include valid ones
          const cleanedExpandedNodes = cleanupExpandedNodes(categoriesData, rootIds);
          setExpandedNodes(cleanedExpandedNodes);
        } else {
          console.log('Unexpected data structure:', categoriesData);
          toast({
            title: "خطأ",
            description: "بيانات الفئات غير صحيحة",
            status: "error",
            duration: 5000,
            isClosable: true,
          });
        }
      } else {
        console.log('API returned success: false, message:', response.message);
        toast({
          title: "خطأ",
          description: response.message || "حدث خطأ أثناء جلب الفئات",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
      toast({
        title: "خطأ",
        description: "حدث خطأ أثناء جلب الفئات",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggleExpand = async (categoryId) => {
    const isCurrentlyExpanded = expandedNodes.includes(categoryId);
    
    if (isCurrentlyExpanded) {
      // Collapse: just remove from expanded nodes
      setExpandedNodes(prev => prev.filter(id => id !== categoryId));
    } else {
      // Expand: add to expanded nodes and fetch children if needed
      setExpandedNodes(prev => [...prev, categoryId]);
      
      // Find the category and check if it has children
      const category = findCategoryById(categories, categoryId);
      if (category && (!category.children || category.children.length === 0)) {
        // Only fetch children if the category actually indicates it has children
        const hasChildren = (category.children && category.children.length > 0) || 
                           (category.has_children === true && category.children_count > 0) ||
                           (category.children_count && category.children_count > 0);
        
        if (hasChildren) {
          // Fetch children for this category
          try {
            const response = await categoriesService.getCategoriesByParent(categoryId);
            if (response.success) {
              const childrenData = response.data?.data || response.data || [];
              if (Array.isArray(childrenData) && childrenData.length > 0) {
                // Update the category with children
                updateCategoryWithChildren(categoryId, childrenData);
              } else {
                // No children found, remove from expanded nodes
                console.log(`Category ${categoryId} has no children, removing from expanded nodes`);
                setExpandedNodes(prev => prev.filter(id => id !== categoryId));
              }
            }
          } catch (error) {
            console.error('Error fetching children for category:', categoryId, error);
            // Remove from expanded nodes on error
            setExpandedNodes(prev => prev.filter(id => id !== categoryId));
          }
        } else {
          // Category doesn't actually have children, remove from expanded nodes
          console.log(`Category ${categoryId} doesn't have children, removing from expanded nodes`);
          setExpandedNodes(prev => prev.filter(id => id !== categoryId));
        }
      }
    }
  };

  const findCategoryById = (cats, id) => {
    for (const cat of cats) {
      if (cat.id === id) return cat;
      if (cat.children) {
        const found = findCategoryById(cat.children, id);
        if (found) return found;
      }
    }
    return null;
  };

  const updateCategoryWithChildren = (categoryId, children) => {
    setCategories(prevCategories => {
      const updateCategory = (cats) => {
        return cats.map(cat => {
          if (cat.id === categoryId) {
            return { ...cat, children };
          }
          if (cat.children) {
            return { ...cat, children: updateCategory(cat.children) };
          }
          return cat;
        });
      };
      return updateCategory(prevCategories);
    });
  };

  // Clean up expanded nodes for categories that don't actually have children
  const cleanupExpandedNodes = (cats, expandedNodes) => {
    const validExpandedNodes = [];
    
    for (const nodeId of expandedNodes) {
      const category = findCategoryById(cats, nodeId);
      if (category) {
        const hasChildren = (category.children && category.children.length > 0) || 
                           (category.has_children === true && category.children_count > 0) ||
                           (category.children_count && category.children_count > 0);
        
        if (hasChildren) {
          validExpandedNodes.push(nodeId);
        }
      }
    }
    
    return validExpandedNodes;
  };

  const openCreateModal = () => {
    setEditingCategory(null);
    setValidationErrors({});
    onModalOpen();
  };

  const openCreateChildModal = (parentCategory) => {
    setEditingCategory({ parent_id: parentCategory.id });
    setValidationErrors({});
    onModalOpen();
  };

  const openEditModal = (category) => {
    setEditingCategory(category);
    setValidationErrors({});
    onModalOpen();
  };

  const handleModalClose = () => {
    setValidationErrors({});
    onModalClose();
  };

  const openDeleteDialog = (category) => {
    setDeletingCategory(category);
    onDeleteOpen();
  };

  const handleCreateCategory = async (categoryData) => {
    setActionLoading({ create: true });
    setValidationErrors({});
    try {
      const response = await categoriesService.createCategory(categoryData);
      console.log('Create category response:', response);
      console.log('Create category response data:', response.data);
      console.log('Create category response status:', response.status);
      
      if (response.success) {
        toast({
          title: "نجح",
          description: "تم إنشاء الفئة بنجاح",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
        handleModalClose();
        
        // Refresh categories and maintain expanded state
        await fetchCategories();
        
        // If this was a child category, expand the parent
        if (categoryData.parent_category_id) {
          setExpandedNodes(prev => [...prev, categoryData.parent_category_id]);
        }
      } else {
        // Handle validation errors (422)
        if (response.status === 422 && response.data?.data?.error_messages) {
          console.log('Setting validation errors:', response.data.data.error_messages);
          setValidationErrors(response.data.data.error_messages);
        } else if (response.data?.error_messages) {
          // Handle case where error_messages is directly in response.data
          console.log('Setting validation errors (direct):', response.data.error_messages);
          setValidationErrors(response.data.error_messages);
        } else {
          toast({
            title: "خطأ",
            description: response.message || "حدث خطأ أثناء إنشاء الفئة",
            status: "error",
            duration: 5000,
            isClosable: true,
          });
        }
      }
    } catch (error) {
      console.error('Error in handleCreateCategory:', error);
      toast({
        title: "خطأ",
        description: "حدث خطأ غير متوقع",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setActionLoading({ create: false });
    }
  };

  const handleUpdateCategory = async (id, categoryData) => {
    setActionLoading({ update: id });
    setValidationErrors({});
    try {
      const response = await categoriesService.updateCategory(id, categoryData);
      if (response.success) {
        toast({
          title: "نجح",
          description: "تم تحديث الفئة بنجاح",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
        handleModalClose();
        
        // Refresh categories and maintain expanded state
        await fetchCategories();
        
        // Maintain expanded state for the updated category
        setExpandedNodes(prev => {
          if (!prev.includes(id)) {
            return [...prev, id];
          }
          return prev;
        });
      } else {
        // Handle validation errors (422)
        if (response.status === 422 && response.data?.data?.error_messages) {
          setValidationErrors(response.data.data.error_messages);
        } else {
          toast({
            title: "خطأ",
            description: response.message || "حدث خطأ أثناء تحديث الفئة",
            status: "error",
            duration: 5000,
            isClosable: true,
          });
        }
      }
    } catch (error) {
      console.error('Error in handleUpdateCategory:', error);
      toast({
        title: "خطأ",
        description: "حدث خطأ غير متوقع",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setActionLoading({ update: false });
    }
  };

  const handleDeleteCategory = async (id) => {
    setActionLoading({ delete: true });
    try {
      const response = await categoriesService.deleteCategory(id);
      if (response.success) {
        toast({
          title: "نجح",
          description: "تم حذف الفئة بنجاح",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
        onDeleteClose();
        
        // Refresh categories and maintain expanded state
        await fetchCategories();
        
        // Remove the deleted category from expanded nodes
        setExpandedNodes(prev => prev.filter(nodeId => nodeId !== id));
      } else {
        toast({
          title: "خطأ",
          description: response.message || "حدث خطأ أثناء حذف الفئة",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error('Error in handleDeleteCategory:', error);
      toast({
        title: "خطأ",
        description: "حدث خطأ غير متوقع",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setActionLoading({ delete: false });
    }
  };

  const handleEdit = (category) => {
    openEditModal(category);
  };

  const handleDelete = (category) => {
    openDeleteDialog(category);
  };

  return (
    <Box pt={{ base: "130px", md: "80px", xl: "80px" }}>
      <Card>
        <Flex direction="column" w="100%">
          <Flex
            direction={{ base: "column", md: "row" }}
            align="center"
            justify="space-between"
            mb="20px"
            gap="20px"
          >
            <Text fontSize="xl" color={textColor} fontWeight="bold">
              إدارة الفئات
            </Text>
                         <Button
               colorScheme="blue"
               leftIcon={<AddIcon />}
               onClick={openCreateModal}
             >
               إضافة فئة جديدة
             </Button>
          </Flex>

          <Box mb="20px">
            <InputGroup>
              <InputLeftElement pointerEvents="none">
                <SearchIcon color="gray.400" />
              </InputLeftElement>
              <Input
                placeholder="البحث في الفئات..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                borderColor={borderColor}
              />
            </InputGroup>
          </Box>

          <Box
            bg={cardBg}
            borderRadius="lg"
            border="1px solid"
            borderColor={borderColor}
            overflow="hidden"
          >
            {isLoading ? (
              <Flex justify="center" align="center" p={8}>
                <Spinner size="lg" color="blue.500" />
              </Flex>
            ) : filteredCategories.length === 0 ? (
              <Flex justify="center" align="center" p={8}>
                <Text color="gray.500">لا توجد فئات</Text>
              </Flex>
            ) : (
              <VStack spacing={0} align="stretch">
                                 {filteredCategories.map((category) => (
                   <TreeNode
                     key={category.id}
                     category={category}
                     onEdit={handleEdit}
                     onDelete={handleDelete}
                     expandedNodes={expandedNodes}
                     onToggleExpand={handleToggleExpand}
                     onAddChild={openCreateChildModal}
                   />
                 ))}
              </VStack>
            )}
                     </Box>
         </Flex>
       </Card>

       {/* Create/Edit Modal */}
       <CategoryModal
         isOpen={isModalOpen}
         onClose={handleModalClose}
         category={editingCategory}
         onSubmit={editingCategory?.id ? handleUpdateCategory : handleCreateCategory}
         actionLoading={actionLoading}
         validationErrors={validationErrors}
       />

       {/* Delete Confirmation Dialog */}
       <AlertDialog isOpen={isDeleteOpen} onClose={onDeleteClose} isCentered>
         <AlertDialogOverlay bg="blackAlpha.300" backdropFilter="blur(10px)">
           <AlertDialogContent
             mx="20px"
             maxW="500px"
             zIndex={9999}
             position="relative"
             borderRadius="15px"
             boxShadow="0 20px 60px rgba(0, 0, 0, 0.3)"
             border="1px solid"
             borderColor="gray.200"
           >
             <AlertDialogHeader
               fontSize="xl"
               fontWeight="bold"
               textAlign="center"
               borderBottom="1px solid"
               borderColor="gray.200"
               pb="20px"
             >
               حذف الفئة
             </AlertDialogHeader>
             <AlertDialogBody
               pt="20px"
               pb="20px"
               textAlign="center"
               fontSize="md"
               color="gray.600"
             >
               هل أنت متأكد من حذف الفئة <strong>"{deletingCategory?.name}"</strong>؟ لا يمكن التراجع عن هذا الإجراء.
             </AlertDialogBody>
             <AlertDialogFooter
               borderTop="1px solid"
               borderColor="gray.200"
               pt="20px"
               gap="15px"
             >
               <Button
                 onClick={onDeleteClose}
                 size="lg"
                 minW="120px"
                 borderRadius="8px"
                 variant="outline"
                 border="2px solid"
                 borderColor="gray.300"
                 _hover={{ bg: "gray.50", borderColor: "gray.400" }}
               >
                 إلغاء
               </Button>
               <Button
                 colorScheme="red"
                 onClick={() => handleDeleteCategory(deletingCategory?.id)}
                 size="lg"
                 minW="120px"
                 borderRadius="8px"
                 isLoading={actionLoading.delete}
                 loadingText="جاري الحذف..."
                 _hover={{ transform: "translateY(-1px)", boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)" }}
               >
                 حذف
               </Button>
             </AlertDialogFooter>
           </AlertDialogContent>
         </AlertDialogOverlay>
       </AlertDialog>
     </Box>
   );
 }
